import { notFoundAction, permanentRedirectAction, renderAction } from '@rea-argonaut/actions';
import { GetAction } from '@property.com.au/argonaut';
import { fetchPropertyPage } from '../graphql/fetchPropertyPage';
import { createUrqlClient, writeUrqlCacheToExchange } from '../../graphql/urqlClient';
import { getSlugFromPathname } from '../../helpers/getSlugFromPathname';
import { getHead } from './getHead';
import { PropertyPageScreen } from '../screens/PropertyPageScreen';
import { UrlContextProvider } from '../../contexts/UrlContext/UrlContext';
import { PropertyPageModalContextProvider } from '../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { Provider as UrqlProvider } from 'urql';
import { trackPropertyPageView } from './trackPropertyPageView';
import { PropertyPageAdContextProvider } from '../contexts/PropertyPageAdContextProvider/PropertyPageAdContextProvider';
import { PropertyIdContext } from '../../contexts/PropertyIdContext/PropertyIdContext';
import { fetchAccount } from '../../graphql/account/fetchAccount';
import { fetchHeaderCtasBySlug } from '../graphql/fetchHeaderCtasBySlug';
import { logger, setTransactionMetadata } from '@rea-argonaut/observability';
import { MediaGalleryModalContextProvider } from '../contexts/MediaGalleryModalContext/MediaGalleryModalContext';
import { isNotNullOrUndefinedValue } from '../../helpers/isNotNullOrUndefinedValue';

export type PropertyPagePathParams = {
  state: 'vic' | 'nsw' | 'nt' | 'qld' | 'sa' | 'tas' | 'wa' | 'act';
  suburbAndPostcode: string;
  street: string;
  propertyNumber: string;
  propertyId: string;
};

export const getAction: GetAction<PropertyPagePathParams> = async ({
  url,
  user,
  pathParams: { propertyId },
  optimizelyManager,
}) => {
  const urqlClient = await createUrqlClient();
  const slug = getSlugFromPathname(url.pathname);
  const { lockeId, isSignedIn } = user;
  const showAVMEnabled = optimizelyManager.isFeatureEnabled('show_avm_data');
  const toggleShowAVMData = isSignedIn || showAVMEnabled;
  const toggleShowOriginNbnBrick = optimizelyManager.isFeatureEnabled('origin_nbn_brick');
  const togglePcaChat = optimizelyManager.isFeatureEnabled('property_chat');
  const toggleMainImageDataMigration = optimizelyManager.isFeatureEnabled(
    'similar_and_neighbouring_properties_image_data_migration',
  );
  const isPropertyReportEnabled = optimizelyManager.isFeatureEnabled('property_report', {
    lockeId: lockeId ?? '',
  });
  const toggleBYBLegalDisclaimers = optimizelyManager.isFeatureEnabled('byb_legal_disclaimers');
  const toggleGovernmentOverlaysUpdate = optimizelyManager.isFeatureEnabled(
    'government_overlays_update',
  );
  const toggleAllianzRenewalBanner = optimizelyManager.isFeatureEnabled('allianz_renewal_banner');

  const propertyPagePromise = fetchPropertyPage({
    client: urqlClient,
    slug,
    propertyId,
    toggleShowAVMData,
    toggleShowOriginNbnBrick,
    toggleMainImageDataMigration,
    togglePropertyReport: isPropertyReportEnabled,
    toggleBYBLegalDisclaimers,
    togglePcaChat,
    toggleGovernmentOverlaysUpdate,
    toggleAllianzRenewalBanner,
  });
  // Make these queries here as the data is required for server side rendering
  const accountPromise = fetchAccount({ client: urqlClient });
  const headerCtasBySlugPromise = fetchHeaderCtasBySlug({
    client: urqlClient,
    slug: getSlugFromPathname(url.pathname),
    propertyId,
    isPropertyReportEnabled,
  });

  const [propertyPageResult, accountResult, headerCtasBySlugResult] = await Promise.all([
    propertyPagePromise,
    accountPromise,
    headerCtasBySlugPromise,
  ]);

  const { data, error } = propertyPageResult;

  if (error?.networkError) {
    throw new Error('Could not successfully connect to Experience API');
  }

  if (accountResult.error?.networkError) {
    throw new Error('Could not retrieve account data from Experience API');
  }

  if (headerCtasBySlugResult.error?.networkError) {
    logger.error({
      error: 'Could not retrieve header CTAs from Experience API',
      customAttributes: { slug, propertyId },
    });
  }

  if (!data?.propertyPageBySlugV3) {
    throw Error('No property page data returned from Experience API');
  }

  if (data.propertyPageBySlugV3.__typename === 'ErrorPage') {
    if (data.propertyPageBySlugV3.errorCode === 404) {
      return notFoundAction();
    }
    throw Error('No property page data returned from Experience API');
  }

  if (data.propertyPageBySlugV3.__typename === 'Redirect') {
    return permanentRedirectAction(`/${data.propertyPageBySlugV3.redirectPath}`);
  }

  const propertyStatus = data.propertyPageBySlugV3.header?.status?.badgeV2.text;
  if (isNotNullOrUndefinedValue(propertyStatus)) {
    setTransactionMetadata({
      newRelicKey: 'propertyStatus',
      value: propertyStatus,
    });
  }

  await writeUrqlCacheToExchange();

  const { helmet, tracking } = data.propertyPageBySlugV3;

  return renderAction(async () => {
    if (tracking) {
      const propertyContext = tracking?.propertyContext ?? tracking?.propertyContextV2;

      if (propertyContext) {
        const { tracked_relationship_typeV2, ...data } = propertyContext.data;

        trackPropertyPageView(
          {
            ...tracking,
            propertyContext: propertyContext
              ? {
                  ...propertyContext,
                  data: {
                    ...data,
                    tracked_relationship_type: tracked_relationship_typeV2,
                  },
                }
              : undefined,
          },
          user,
        );
      }
    }

    return {
      getElements: async () => {
        return {
          Screen: (
            <UrqlProvider value={urqlClient}>
              <UrlContextProvider url={url}>
                <PropertyIdContext.Provider value={propertyId}>
                  <PropertyPageAdContextProvider>
                    <PropertyPageModalContextProvider>
                      <MediaGalleryModalContextProvider>
                        <PropertyPageScreen isSignedIn={isSignedIn} lockeId={lockeId} />
                      </MediaGalleryModalContextProvider>
                    </PropertyPageModalContextProvider>
                  </PropertyPageAdContextProvider>
                </PropertyIdContext.Provider>
              </UrlContextProvider>
            </UrqlProvider>
          ),
        };
      },
      getHead: async () => getHead(helmet),
    };
  });
};
